import { useRouter } from '@tanstack/react-router';
import { Badge, Box, Button, Heading, Image, InlineStack } from '@customerio/pluma-components/react';
import { getWarehouseDestinationImageSrc, getWarehouseDestinationName } from '@/features/warehouse-sync';
import { warehouseSyncType, type WarehouseDestination, type WarehouseSync } from '@/services/db/warehouse-sync/entity';
import * as styles from './list-status.css';

/**
 * The bucket providers a connector landing page offers. Yandex is a valid sync
 * destination everywhere else but is deliberately absent here — the landing
 * pages only advertise these three.
 */
const OFFERED_DESTINATIONS: readonly WarehouseDestination[] = ['s3', 'gcs', 'azure_blob'];

type ListStatusProps = {
  workspaceId: string;
  sync: WarehouseSync | null;
};

/**
 * The "sync status" block on a connector landing page: one row per bucket
 * provider, or — once a sync exists — only the row it writes to.
 *
 * Both CTAs point at the migrated URLs — the whole subtree is Hydra-owned as of
 * FPS-2730, and the `journeys` twins forward here for any Ember-side link.
 */
export function ListStatus({ workspaceId, sync }: ListStatusProps) {
  const router = useRouter();
  const configured = warehouseSyncType(sync);
  // A sync to a provider this list does not offer (Yandex) leaves every row
  // unconfigured, so the page shows the full "connect one" list.
  const isAnyConfigured = OFFERED_DESTINATIONS.some((destination) => destination === configured);
  const rows = isAnyConfigured ? OFFERED_DESTINATIONS.filter((d) => d === configured) : OFFERED_DESTINATIONS;

  return (
    <Box>
      {rows.map((destination) => {
        const isConfigured = destination === configured;
        // The config view is Hydra-owned (FPS-2717); the create wizard is still Ember-served.
        const manageHref = router.buildLocation({
          to: '/workspaces/$workspaceId/integrations/warehouse/$type',
          params: { workspaceId, type: destination },
        }).href;
        const connectHref = router.buildLocation({
          to: '/workspaces/$workspaceId/integrations/warehouse/$type/sync',
          params: { workspaceId, type: destination },
        }).href;

        return (
          <div key={destination} className={styles.row}>
            <Image src={getWarehouseDestinationImageSrc(destination)} alt="" size="icon-v2-xl" mx="200" />
            <InlineStack alignBlock="center" gap="100" className={styles.body}>
              <Heading level="3" color="bold">
                {getWarehouseDestinationName(destination)}
              </Heading>
              {isConfigured ? (
                <Badge color="green" showStatusIndicator data-test-warehouse-status>
                  Enabled
                </Badge>
              ) : null}
              <Box flex={1} />
              {isConfigured ? (
                <Button href={manageHref} variant="primary" size="sm" icon="chevron-right" iconPosition="trailing">
                  Manage sync
                </Button>
              ) : (
                <Button href={connectHref} variant="secondary" size="sm" icon="chevron-right" iconPosition="trailing">
                  Sync bucket
                </Button>
              )}
            </InlineStack>
          </div>
        );
      })}
    </Box>
  );
}
